/**
 * KioskDashboard – Tiered "Venue Brain" Dashboard
 *
 * Staff tier   – Read-only data (board, specials, stock 86) + My Tasks + Training Status.
 *                All mutating controls disabled / hidden.
 * Manager tier – Full CRUD: Manager Board, Stock 86 toggle/add/delete, OPS Specials.
 *
 * Role gate is performed client-side using the role returned by /kiosk/pin-login.
 * MANAGER_ROLES = super_admin | ops | gm | manager
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  LogOut, Cloud, Trophy, MessageSquare, ToggleLeft, ToggleRight,
  Plus, Trash2, ChefHat, Clock, Loader2, RefreshCw, AlertTriangle,
  BookOpen, CheckSquare, Lock,
} from 'lucide-react';
import { useKioskStore, MANAGER_ROLES } from '../lib/kiosk';
import { kioskAPI } from '../lib/api';
import EHOVaultButton from '../components/ui/EHOVaultButton';

// ── 14 Legal Allergens (UK Food Information Regulations) ──
const ALLERGENS = [
  'Celery', 'Gluten', 'Crustaceans', 'Eggs', 'Fish',
  'Lupin', 'Milk', 'Molluscs', 'Mustard', 'Nuts',
  'Peanuts', 'Sesame', 'Soya', 'Sulphites',
];

const KIOSK_INCIDENT_TYPES = [
  'customer_accident',
  'staff_injury',
  'altercation',
  'fire_evacuation',
  'medical_emergency',
  'pest_sighting',
  'equipment_failure',
  'near_miss',
  'property_damage',
  'theft',
  'other',
];

const toIncidentLabel = (value) => (value || '').replace(/_/g, ' ');

// ──────────────────────────────────────────────
// Role gate helper — used throughout
// ──────────────────────────────────────────────
function ReadOnlyBadge() {
  return (
    <span className="inline-flex items-center gap-1 text-[#52525B] text-xs">
      <Lock className="w-3 h-3" />View only
    </span>
  );
}

// ──────────────────────────────────────────────
// Manager Message Board
// ──────────────────────────────────────────────
function MessageBoard({ venueId, isManager }) {
  const [message, setMessage]   = useState('');
  const [saved, setSaved]       = useState('');
  const [loading, setLoading]   = useState(false);
  const [saving, setSaving]     = useState(false);

  useEffect(() => {
    if (!venueId) return;
    setLoading(true);
    kioskAPI.getMessageBoard(venueId)
      .then((r) => setMessage(r.data?.message || ''))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [venueId]);

  const save = async () => {
    if (!venueId || !isManager) return;
    setSaving(true);
    try {
      await kioskAPI.setMessageBoard(venueId, message);
      setSaved('Saved');
      setTimeout(() => setSaved(''), 2000);
    } catch {
      setSaved('Error saving');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-[#18181B] border border-[#27272A] rounded-2xl p-4">
      <div className="flex items-center gap-2 mb-3">
        <MessageSquare className="w-5 h-5 text-[#FF6600]" />
        <h3 className="text-white font-bold text-sm uppercase tracking-wide">Manager Board</h3>
        {!isManager && <ReadOnlyBadge />}
        {saved && <span className="text-green-400 text-xs ml-auto">{saved}</span>}
      </div>
      {loading ? (
        <div className="flex items-center gap-2 text-[#52525B] text-sm py-4">
          <Loader2 className="w-4 h-4 animate-spin" />Loading board…
        </div>
      ) : isManager ? (
        <>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Today's priorities, reminders, briefing notes…"
            rows={4}
            className="w-full bg-[#0A0A0A] border border-[#3F3F46] rounded-xl p-3 text-white text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#FF6600] placeholder:text-[#52525B]"
          />
          <button onClick={save} disabled={saving}
            className="mt-2 w-full py-2 bg-[#FF6600]/20 hover:bg-[#FF6600]/30 text-[#FF6600] font-semibold text-sm rounded-xl border border-[#FF6600]/30 transition-colors disabled:opacity-50">
            {saving ? 'Saving…' : 'Save Note'}
          </button>
        </>
      ) : (
        <p className="text-[#D1D1D1] text-sm whitespace-pre-line leading-relaxed min-h-[3rem]">
          {message || <span className="text-[#52525B] italic">No message posted yet.</span>}
        </p>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────
// Stock 86 Toggle
// ──────────────────────────────────────────────
function StockToggle({ venueId, isManager }) {
  const [items, setItems]     = useState([]);
  const [loading, setLoading] = useState(false);
  const [newItem, setNewItem] = useState('');
  const [newDept, setNewDept] = useState('kitchen');
  const [adding, setAdding]   = useState(false);

  const load = useCallback(() => {
    if (!venueId) return;
    setLoading(true);
    kioskAPI.getStock86(venueId)
      .then((r) => setItems(r.data?.items || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [venueId]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (id, current) => {
    if (!isManager) return;
    try {
      await kioskAPI.updateStock86(venueId, id, !current);
      setItems((prev) => prev.map((it) => it.id === id ? { ...it, available: !current } : it));
    } catch {}
  };

  const addItem = async () => {
    if (!newItem.trim() || !isManager) return;
    setAdding(true);
    try {
      const r = await kioskAPI.addStock86Item(venueId, newItem.trim(), newDept);
      setItems((prev) => [...prev, r.data]);
      setNewItem('');
    } catch {}
    setAdding(false);
  };

  const remove = async (id) => {
    if (!isManager) return;
    try {
      await kioskAPI.removeStock86Item(venueId, id);
      setItems((prev) => prev.filter((it) => it.id !== id));
    } catch {}
  };

  const kitchen = items.filter((i) => i.department === 'kitchen');
  const bar     = items.filter((i) => i.department === 'bar');

  return (
    <div className="bg-[#18181B] border border-[#27272A] rounded-2xl p-4">
      <div className="flex items-center gap-2 mb-3">
        <ChefHat className="w-5 h-5 text-[#FF6600]" />
        <h3 className="text-white font-bold text-sm uppercase tracking-wide">Stock 86</h3>
        {!isManager && <ReadOnlyBadge />}
        <button onClick={load} className="ml-auto text-[#52525B] hover:text-white">
          <RefreshCw className="w-3 h-3" />
        </button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-[#52525B] text-sm py-2">
          <Loader2 className="w-3 h-3 animate-spin" />Loading items…
        </div>
      ) : (
        <>
          {[['🍳 Kitchen', kitchen], ['🍹 Bar', bar]].map(([label, list]) => (
            <div key={label} className="mb-3">
              <p className="text-[#71717A] text-xs uppercase font-semibold mb-1">{label}</p>
              {list.length === 0 && <p className="text-[#3F3F46] text-xs italic">No items</p>}
              {list.map((it) => (
                <div key={it.id} className="flex items-center justify-between py-2 border-b border-[#27272A] last:border-0">
                  <span className={`text-sm font-medium ${it.available ? 'text-white' : 'text-[#71717A] line-through'}`}>
                    {it.name}
                  </span>
                  <div className="flex items-center gap-3">
                    {isManager ? (
                      <>
                        <button onClick={() => toggle(it.id, it.available)}
                          className={`text-xs font-bold px-3 py-1 rounded-full transition-colors ${
                            it.available
                              ? 'bg-green-900/40 text-green-400 hover:bg-green-900/60'
                              : 'bg-red-900/40 text-red-400 hover:bg-red-900/60'
                          }`}>
                          {it.available ? 'IN' : '86\'d'}
                        </button>
                        <button onClick={() => remove(it.id)} className="text-[#52525B] hover:text-red-400 transition-colors">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </>
                    ) : (
                      <span className={`text-xs font-bold px-3 py-1 rounded-full ${
                        it.available ? 'bg-green-900/30 text-green-500' : 'bg-red-900/30 text-red-400'
                      }`}>
                        {it.available ? 'IN' : '86\'d'}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </>
      )}

      {/* Add item — manager only */}
      {isManager && (
        <div className="flex gap-2 mt-3">
          <select value={newDept} onChange={(e) => setNewDept(e.target.value)}
            className="bg-[#27272A] border border-[#3F3F46] text-white text-xs rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-[#FF6600]">
            <option value="kitchen">Kitchen</option>
            <option value="bar">Bar</option>
          </select>
          <input
            value={newItem}
            onChange={(e) => setNewItem(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addItem()}
            placeholder="Item name…"
            className="flex-1 bg-[#27272A] border border-[#3F3F46] text-white text-xs rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-[#FF6600] placeholder:text-[#52525B]"
          />
          <button onClick={addItem} disabled={adding || !newItem.trim()}
            className="bg-[#FF6600] hover:bg-[#e55a00] disabled:opacity-40 text-white rounded-lg px-3 py-1">
            {adding ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
          </button>
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────
// OPS Menu Adder / Daily Specials
// ──────────────────────────────────────────────
function OPSMenuAdder({ venueId, isManager }) {
  const blank = { name: '', description: '', price: '', allergens: [] };
  const [specials, setSpecials] = useState([]);
  const [form, setForm]         = useState(blank);
  const [saving, setSaving]     = useState(false);
  const [loading, setLoading]   = useState(false);

  const load = useCallback(() => {
    if (!venueId) return;
    setLoading(true);
    kioskAPI.getMenuSpecials(venueId)
      .then((r) => setSpecials(r.data?.specials || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [venueId]);

  useEffect(() => { load(); }, [load]);

  const toggleAllergen = (a) => {
    if (!isManager) return;
    setForm((f) => ({
      ...f,
      allergens: f.allergens.includes(a) ? f.allergens.filter((x) => x !== a) : [...f.allergens, a],
    }));
  };

  const addSpecial = async () => {
    if (!form.name.trim() || !isManager) return;
    setSaving(true);
    try {
      const r = await kioskAPI.addMenuSpecial(venueId, form);
      setSpecials((prev) => [r.data, ...prev]);
      setForm(blank);
    } catch {}
    setSaving(false);
  };

  const remove = async (id) => {
    if (!isManager) return;
    try {
      await kioskAPI.removeMenuSpecial(venueId, id);
      setSpecials((prev) => prev.filter((s) => s.id !== id));
    } catch {}
  };

  return (
    <div className="bg-[#18181B] border border-[#27272A] rounded-2xl p-4">
      <div className="flex items-center gap-2 mb-3">
        <Plus className="w-5 h-5 text-[#FF6600]" />
        <h3 className="text-white font-bold text-sm uppercase tracking-wide">Daily Specials</h3>
        {!isManager && <ReadOnlyBadge />}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-[#52525B] text-sm mb-3">
          <Loader2 className="w-3 h-3 animate-spin" />Loading specials…
        </div>
      ) : specials.length > 0 ? (
        <div className="space-y-2 mb-4 max-h-40 overflow-y-auto">
          {specials.map((s) => (
            <div key={s.id} className="flex items-start justify-between gap-2 p-2 bg-[#27272A] rounded-xl">
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-semibold break-words">{s.name}</p>
                {s.price && <p className="text-[#FF6600] text-xs">£{s.price}</p>}
                {s.allergens?.length > 0 && (
                  <p className="text-[#71717A] text-xs mt-1 break-words">⚠ {s.allergens.join(', ')}</p>
                )}
              </div>
              {isManager && (
                <button onClick={() => remove(s.id)} className="text-[#52525B] hover:text-red-400 ml-2 flex-shrink-0">
                  <Trash2 className="w-3 h-3" />
                </button>
              )}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-[#3F3F46] text-xs italic mb-3">No specials today</p>
      )}

      {/* Add form — manager only */}
      {isManager && (
        <div className="space-y-2">
          <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="Special name *"
            className="w-full bg-[#0A0A0A] border border-[#3F3F46] text-white text-sm rounded-xl px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#FF6600] placeholder:text-[#52525B]" />
          <input value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            placeholder="Description / upsell note"
            className="w-full bg-[#0A0A0A] border border-[#3F3F46] text-white text-sm rounded-xl px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#FF6600] placeholder:text-[#52525B]" />
          <input value={form.price} onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))}
            placeholder="Price (optional)"
            className="w-full bg-[#0A0A0A] border border-[#3F3F46] text-white text-sm rounded-xl px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#FF6600] placeholder:text-[#52525B]" />
          <div>
            <p className="text-[#71717A] text-xs uppercase font-semibold mb-1">Allergens (UK Food Law)</p>
            <div className="flex flex-wrap gap-1">
              {ALLERGENS.map((a) => (
                <button key={a} type="button" onClick={() => toggleAllergen(a)}
                  className={`px-2 py-0.5 rounded-full text-xs font-semibold border transition-colors ${
                    form.allergens.includes(a)
                      ? 'bg-[#FF6600] border-[#FF6600] text-white'
                      : 'bg-transparent border-[#3F3F46] text-[#71717A] hover:border-[#FF6600]/50'
                  }`}>
                  {a}
                </button>
              ))}
            </div>
          </div>
          <button onClick={addSpecial} disabled={saving || !form.name.trim()}
            className="w-full py-2 bg-[#FF6600] hover:bg-[#e55a00] disabled:opacity-40 text-white font-bold text-sm rounded-xl transition-colors flex items-center justify-center gap-2">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Add Special
          </button>
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────
// External Widgets (Post-PIN) – matches ambient
// ──────────────────────────────────────────────
function ApiSportsGamesWidget() {
  const apiSportsKey = process.env.REACT_APP_API_SPORTS_KEY || '';

  useEffect(() => {
    const id = 'api-sports-widgets-js';
    if (document.getElementById(id)) return;
    const script = document.createElement('script');
    script.id = id;
    script.type = 'module';
    script.src = 'https://widgets.api-sports.io/3.1.0/widgets.js';
    document.body.appendChild(script);
  }, []);

  if (!apiSportsKey) {
    return (
      <div className="bg-[#18181B] border border-[#27272A] rounded-2xl p-4">
        <p className="text-[#71717A] text-xs">Set <code>REACT_APP_API_SPORTS_KEY</code> to enable today's fixtures widget.</p>
      </div>
    );
  }

  return (
    <div className="bg-[#18181B] border border-[#27272A] rounded-2xl p-2 space-y-2">
      <h2 className="text-[#FF6600] text-sm font-bold uppercase tracking-wide px-1">Today's Big Games</h2>
      <api-sports-widget
        data-type="config"
        data-key={apiSportsKey}
        data-sport="football"
        data-theme="dark"
        data-timezone="Europe/London"
        data-show-logos="true"
      />
      <api-sports-widget data-type="games" data-league="39" data-tab="today" data-show-toolbar="false" />
      <api-sports-widget data-type="games" data-league="40" data-tab="today" data-show-toolbar="false" />
    </div>
  );
}

function WeatherApiWidget() {
  const weatherKey = process.env.REACT_APP_WEATHER_API_KEY || '';
  const [weather, setWeather] = useState(null);
  const [weatherError, setWeatherError] = useState(false);

  useEffect(() => {
    if (!weatherKey) return;
    setWeather(null);
    setWeatherError(false);
    fetch(`https://api.weatherapi.com/v1/current.json?key=${weatherKey}&q=auto:ip`)
      .then((r) => r.json())
      .then((data) => setWeather(data))
      .catch(() => setWeatherError(true));
  }, [weatherKey]);

  if (!weatherKey) {
    return (
      <div className="bg-[#18181B] border border-[#27272A] rounded-2xl p-4">
        <p className="text-[#71717A] text-xs">Set <code>REACT_APP_WEATHER_API_KEY</code> to enable live weather.</p>
      </div>
    );
  }

  if (weatherError) {
    return (
      <div className="bg-[#18181B] border border-[#27272A] rounded-2xl p-4">
        <p className="text-[#71717A] text-xs">Weather unavailable.</p>
      </div>
    );
  }

  if (!weather) {
    return (
      <div className="bg-[#18181B] border border-[#27272A] rounded-2xl p-4 flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin text-[#71717A]" />
        <span className="text-[#71717A] text-xs">Detecting location…</span>
      </div>
    );
  }

  return (
    <div className="bg-[#18181B] border border-[#27272A] rounded-2xl p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
      <div className="min-w-0">
        <div className="text-white font-bold text-sm break-words">{weather.location.name}</div>
        <div className="text-[#A1A1AA] text-xs break-words">{weather.current.condition.text}</div>
      </div>
      <div className="flex items-center gap-2 w-full sm:w-auto justify-between sm:justify-start">
        <img src={`https:${weather.current.condition.icon}`} alt="" width={48} />
        <div className="text-white text-2xl font-bold">{Math.round(weather.current.temp_c)}°C</div>
      </div>
    </div>
  );
}

function KioskExternalWidgets() {
  return (
    <>
      <ApiSportsGamesWidget />
      <WeatherApiWidget />
    </>
  );
}

// ──────────────────────────────────────────────
// My Tasks (staff tier)
// ──────────────────────────────────────────────
const STATUS_COLOURS = {
  pending:     'text-yellow-400 bg-yellow-900/30',
  in_progress: 'text-blue-400 bg-blue-900/30',
  overdue:     'text-red-400 bg-red-900/30',
};

function MyTasksPanel({ userId, venueId }) {
  const [tasks, setTasks]     = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!userId || !venueId) return;
    setLoading(true);
    kioskAPI.getStaffTasks(userId, venueId)
      .then((r) => setTasks(r.data?.tasks || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [userId, venueId]);

  return (
    <div className="bg-[#18181B] border border-[#27272A] rounded-2xl p-4">
      <div className="flex items-center gap-2 mb-3">
        <CheckSquare className="w-5 h-5 text-[#FF6600]" />
        <h3 className="text-white font-bold text-sm uppercase tracking-wide">My Tasks</h3>
        <ReadOnlyBadge />
      </div>
      {loading ? (
        <div className="flex items-center gap-2 text-[#52525B] text-sm py-2">
          <Loader2 className="w-3 h-3 animate-spin" />Loading tasks…
        </div>
      ) : tasks.length === 0 ? (
        <p className="text-[#3F3F46] text-xs italic">No active tasks assigned today.</p>
      ) : (
        <div className="space-y-2">
          {tasks.map((t) => (
            <div key={t.id} className="flex flex-col sm:flex-row items-start justify-between gap-3 p-2 bg-[#27272A] rounded-xl">
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium break-words">{t.title}</p>
                {t.deadline && (
                  <p className="text-[#71717A] text-xs mt-0.5">
                    Due {new Date(t.deadline).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                  </p>
                )}
              </div>
              <span className={`text-xs font-bold px-2 py-0.5 rounded-full shrink-0 capitalize ${STATUS_COLOURS[t.status] || 'text-[#71717A] bg-[#27272A]'}`}>
                {t.status?.replace('_', ' ')}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────
// Training Status (staff tier)
// ──────────────────────────────────────────────
function TrainingPanel({ userId, venueId }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!userId || !venueId) return;
    setLoading(true);
    kioskAPI.getStaffTraining(userId, venueId)
      .then((r) => setRecords(r.data?.records || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [userId, venueId]);

  return (
    <div className="bg-[#18181B] border border-[#27272A] rounded-2xl p-4">
      <div className="flex items-center gap-2 mb-3">
        <BookOpen className="w-5 h-5 text-[#FF6600]" />
        <h3 className="text-white font-bold text-sm uppercase tracking-wide">Training Status</h3>
      </div>
      {loading ? (
        <div className="flex items-center gap-2 text-[#52525B] text-sm py-2">
          <Loader2 className="w-3 h-3 animate-spin" />Loading training…
        </div>
      ) : records.length === 0 ? (
        <p className="text-[#3F3F46] text-xs italic">No training records on file.</p>
      ) : (
        <div className="space-y-2">
          {records.map((r, i) => (
            <div key={i} className="flex items-center justify-between gap-3">
              <p className="text-white text-sm break-words flex-1">{r.certificate_name}</p>
              <div className="flex flex-wrap items-center gap-2 shrink-0">
                {r.expiry_date && (
                  <span className="text-[#71717A] text-xs">{r.expiry_date.slice(0, 10)}</span>
                )}
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                  r.status === 'expired'       ? 'text-red-400 bg-red-900/30'
                  : r.status === 'expiring_soon' ? 'text-yellow-400 bg-yellow-900/30'
                  : 'text-green-400 bg-green-900/30'
                }`}>
                  {r.status?.replace('_', ' ')}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────
// Incident Reports (all kiosk users)
// ──────────────────────────────────────────────
function IncidentPanel({ userId, venueId }) {
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    incident_type: 'other',
    occurred_at: new Date().toISOString().slice(0, 16),
    location: '',
    description: '',
    action_taken: '',
    emergency_services: false,
    cctv_captured: false,
  });

  const load = useCallback(() => {
    if (!venueId) return;
    setLoading(true);
    kioskAPI.getIncidents(venueId, 20)
      .then((r) => setIncidents(r.data?.incidents || []))
      .catch(() => setError('Failed to load incidents'))
      .finally(() => setLoading(false));
  }, [venueId]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!venueId || !userId) return;
    if (!form.location.trim() || !form.description.trim() || !form.action_taken.trim()) {
      setError('Location, description and action taken are required');
      return;
    }

    setSaving(true);
    setError('');
    try {
      await kioskAPI.createIncident({
        venue_id: venueId,
        user_id: userId,
        incident_type: form.incident_type,
        occurred_at: new Date(form.occurred_at).toISOString(),
        location: form.location.trim(),
        description: form.description.trim(),
        action_taken: form.action_taken.trim(),
        emergency_services: form.emergency_services,
        cctv_captured: form.cctv_captured,
        witness_details: null,
        evidence_urls: [],
      });
      setForm((f) => ({
        ...f,
        incident_type: 'other',
        location: '',
        description: '',
        action_taken: '',
        emergency_services: false,
        cctv_captured: false,
      }));
      load();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to submit incident');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-[#18181B] border border-[#27272A] rounded-2xl p-4 space-y-3">
      <div className="flex items-center gap-2">
        <AlertTriangle className="w-5 h-5 text-[#FF6600]" />
        <h3 className="text-white font-bold text-sm uppercase tracking-wide">Incident Report</h3>
        <button onClick={load} className="ml-auto text-[#52525B] hover:text-white">
          <RefreshCw className="w-3 h-3" />
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <select
          value={form.incident_type}
          onChange={(e) => setForm((f) => ({ ...f, incident_type: e.target.value }))}
          className="bg-[#0A0A0A] border border-[#3F3F46] text-white text-xs rounded-lg px-2 py-2 focus:outline-none focus:ring-1 focus:ring-[#FF6600]"
        >
          {KIOSK_INCIDENT_TYPES.map((type) => (
            <option key={type} value={type}>{toIncidentLabel(type)}</option>
          ))}
        </select>
        <input
          type="datetime-local"
          value={form.occurred_at}
          onChange={(e) => setForm((f) => ({ ...f, occurred_at: e.target.value }))}
          className="bg-[#0A0A0A] border border-[#3F3F46] text-white text-xs rounded-lg px-2 py-2 focus:outline-none focus:ring-1 focus:ring-[#FF6600]"
        />
      </div>

      <input
        value={form.location}
        onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
        placeholder="Location *"
        className="w-full bg-[#0A0A0A] border border-[#3F3F46] text-white text-xs rounded-lg px-2 py-2 focus:outline-none focus:ring-1 focus:ring-[#FF6600] placeholder:text-[#52525B]"
      />
      <textarea
        value={form.description}
        onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
        placeholder="Description *"
        rows={3}
        className="w-full bg-[#0A0A0A] border border-[#3F3F46] text-white text-xs rounded-lg px-2 py-2 focus:outline-none focus:ring-1 focus:ring-[#FF6600] placeholder:text-[#52525B]"
      />
      <textarea
        value={form.action_taken}
        onChange={(e) => setForm((f) => ({ ...f, action_taken: e.target.value }))}
        placeholder="Action taken *"
        rows={2}
        className="w-full bg-[#0A0A0A] border border-[#3F3F46] text-white text-xs rounded-lg px-2 py-2 focus:outline-none focus:ring-1 focus:ring-[#FF6600] placeholder:text-[#52525B]"
      />

      <div className="flex flex-wrap items-center gap-3 text-xs text-[#A1A1AA]">
        <label className="flex items-center gap-1">
          <input
            type="checkbox"
            checked={form.cctv_captured}
            onChange={(e) => setForm((f) => ({ ...f, cctv_captured: e.target.checked }))}
          />
          CCTV
        </label>
        <label className="flex items-center gap-1">
          <input
            type="checkbox"
            checked={form.emergency_services}
            onChange={(e) => setForm((f) => ({ ...f, emergency_services: e.target.checked }))}
          />
          Emergency services
        </label>
      </div>

      {error && <p className="text-red-400 text-xs">{error}</p>}

      <button
        onClick={submit}
        disabled={saving}
        className="w-full py-2 bg-[#FF6600] hover:bg-[#e55a00] disabled:opacity-40 text-white font-bold text-sm rounded-xl transition-colors flex items-center justify-center gap-2"
      >
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
        Submit Incident
      </button>

      <div className="pt-2 border-t border-[#27272A]">
        {loading ? (
          <div className="flex items-center gap-2 text-[#52525B] text-sm py-2">
            <Loader2 className="w-3 h-3 animate-spin" />Loading incidents…
          </div>
        ) : incidents.length === 0 ? (
          <p className="text-[#3F3F46] text-xs italic">No incidents logged yet.</p>
        ) : (
          <div className="space-y-2 max-h-52 overflow-y-auto pr-1">
            {incidents.map((incident) => (
              <div key={incident.id} className="bg-[#27272A] rounded-xl p-2">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                  <p className="text-white text-xs font-semibold capitalize break-words">
                    {toIncidentLabel(incident.incident_type)}
                  </p>
                  <span className="text-[#71717A] text-[10px] shrink-0">
                    {incident.occurred_at ? new Date(incident.occurred_at).toLocaleString('en-GB') : '—'}
                  </span>
                </div>
                <p className="text-[#A1A1AA] text-[11px] mt-1 break-words">{incident.location || '—'}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────
// Root: KioskDashboard
// ──────────────────────────────────────────────
export default function KioskDashboard({ onLogout }) {
  const { kioskVenueId, kioskVenueName, pinSessionUserId, pinSessionUserName, pinSessionRole } = useKioskStore();
  const isManager = MANAGER_ROLES.has(pinSessionRole);
  const now = new Date();

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white overflow-y-auto">
      {/* Top bar */}
      <div className="sticky top-0 z-30 bg-[#0A0A0A]/95 backdrop-blur border-b border-[#18181B] px-4 py-3 flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[#FF6600] text-xs font-bold uppercase tracking-widest">
            {kioskVenueName || 'Venue Kiosk'}
          </p>
          <p className="text-white font-black text-lg leading-none">Venue Brain</p>
        </div>
        <div className="text-left sm:text-right min-w-0 flex-1 sm:flex-none">
          <p className="text-[#A1A1AA] text-xs break-words">{pinSessionUserName}</p>
          <div className="flex items-center gap-1 justify-end mt-0.5">
            <span className={`text-xs font-bold px-2 py-0.5 rounded-full capitalize ${
              isManager ? 'bg-[#FF6600]/20 text-[#FF6600]' : 'bg-[#27272A] text-[#71717A]'
            }`}>
              {isManager ? '🔓 ' : '🔒 '}{pinSessionRole}
            </span>
          </div>
        </div>
        <button onClick={onLogout}
          className="flex items-center gap-1 text-xs text-[#71717A] hover:text-red-400 transition-colors border border-[#3F3F46] rounded-lg px-2 py-1">
          <LogOut className="w-3 h-3" />End
        </button>
      </div>

      {/* Clock banner */}
      <div className="bg-[#18181B] border-b border-[#27272A] px-4 py-2 flex items-center gap-2 text-[#52525B] text-xs">
        <Clock className="w-3 h-3" />
        {now.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
        &nbsp;·&nbsp;
        {now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
      </div>

      <div className="p-4 space-y-4 max-w-2xl mx-auto pb-10">
        {/* EHO Vault — always accessible */}
        <EHOVaultButton venueId={kioskVenueId} venueName={kioskVenueName} kioskUserId={pinSessionUserId} />

        {/* Shared info widgets */}
        <KioskExternalWidgets />
        <IncidentPanel userId={pinSessionUserId} venueId={kioskVenueId} />

        {/* Staff-only panels */}
        {!isManager && (
          <>
            <MyTasksPanel  userId={pinSessionUserId} venueId={kioskVenueId} />
            <TrainingPanel userId={pinSessionUserId} venueId={kioskVenueId} />
          </>
        )}

        {/* Data panels — read-only for staff, editable for manager */}
        <MessageBoard venueId={kioskVenueId} isManager={isManager} />
        <StockToggle   venueId={kioskVenueId} isManager={isManager} />
        <OPSMenuAdder  venueId={kioskVenueId} isManager={isManager} />
      </div>
    </div>
  );
}
